import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { DRIVER_CREATE, DRIVER_UPDATE } from '../../graphql/drivers';
import { TextField, Button, MenuItem, Box } from '@mui/material';
import EditableFormField from '../../components/editable-form-field';
import DatePickerAuto from '../../components/custom-datepicker/DatePickerAuto';

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const licenseTypeOptions = [
  { value: 'class_a', label: 'Class A' },
  { value: 'class_b', label: 'Class B' },
  { value: 'class_c', label: 'Class C' },
  { value: 'class_d', label: 'Class D' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'cdl', label: 'CDL' },
  { value: 'other', label: 'Other' }
];

const stateOptions = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const DriverForm = ({ driver, onClose, isEditing: externalIsEditing, onToggleEdit }) => {
  const [internalIsEditing, setInternalIsEditing] = useState(!driver); // Start in edit mode for new drivers, view mode for existing
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;
  
  const [form, setForm] = useState({
    name: '',
    licenseNumber: '',
    license_expiration_date: '',
    license_type: '',
    license_issuing_state: '',
    license_full_name: '',
    phone: '',
    status: 'active',
  });

  useEffect(() => {
    if (driver) {
      setForm({
        name: driver.name || '',
        licenseNumber: driver.licenseNumber || '',
        license_expiration_date: driver.license_expiration_date || '',
        license_type: driver.license_type || '',
        license_issuing_state: driver.license_issuing_state || '',
        license_full_name: driver.license_full_name || '',
        phone: driver.phone || '',
        status: driver.status || 'active',
      });
    } else {
      setForm({
        name: '',
        licenseNumber: '',
        license_expiration_date: '',
        license_type: '',
        license_issuing_state: '',
        license_full_name: '',
        phone: '',
        status: 'active',
      });
    }
  }, [driver]);

  const [createDriver, { loading: creating }] = useMutation(DRIVER_CREATE, {
    refetchQueries: ['DriversList'],
  });
  const [updateDriver, { loading: updating }] = useMutation(DRIVER_UPDATE, {
    refetchQueries: ['DriversList'],
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (driver) {
      await updateDriver({ variables: { data: { id: driver.id, ...form } } });
    } else {
      await createDriver({ variables: { data: form } });
    }
    onClose(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (driver) {
      // Reset form to original values
      setForm({
        name: driver.name || '',
        licenseNumber: driver.licenseNumber || '',
        license_expiration_date: driver.license_expiration_date || '',
        license_type: driver.license_type || '',
        license_issuing_state: driver.license_issuing_state || '',
        license_full_name: driver.license_full_name || '',
        phone: driver.phone || '',
        status: driver.status || 'active',
      });
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>

      {/* Edit Button for View Mode - Top */}
      {driver && !isEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={handleEdit} variant="contained" color="primary">
            Edit
          </Button>
        </Box>
      )}

      <EditableFormField
        type="text"
        label="Name"
        name="name"
        value={form.name}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <EditableFormField
        type="text"
        label="License Number"
        name="licenseNumber"
        value={form.licenseNumber}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <EditableFormField
        type="text"
        label="License Full Name"
        name="license_full_name"
        value={form.license_full_name}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <DatePickerAuto
        editTab={isEditing}
        rest={{}}
        value={form.license_expiration_date}
        label="License Expiration Date"
        onUpdate={(date) => {
          setForm(prev => ({ ...prev, license_expiration_date: date }));
        }}
        required
      />

      <EditableFormField
        type="select"
        label="License Type"
        name="license_type"
        value={form.license_type}
        onChange={handleChange}
        isEditing={isEditing}
        options={licenseTypeOptions}
        required
      />

      <EditableFormField
        type="select"
        label="License Issuing State"
        name="license_issuing_state"
        value={form.license_issuing_state}
        onChange={handleChange}
        isEditing={isEditing}
        options={stateOptions}
        required
      />

      <EditableFormField
        type="text"
        label="Phone"
        name="phone"
        value={form.phone}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <EditableFormField
        type="select"
        label="Status"
        name="status"
        value={form.status}
        onChange={handleChange}
        isEditing={isEditing}
        options={statusOptions}
      />

      {/* Action Buttons - Bottom */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={handleCancel} sx={{ mr: 1 }} disabled={creating || updating}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={creating || updating}>
          {driver ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};

export default DriverForm; 